import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateRoadOptionInput, PatchRoadOptionInput, RoadOption } from '@magimp/shared';
import {
  Button,
  DataTable,
  EditorModal,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Select,
  ToolbarActions,
} from '../../../ui/index.js';
import { CancelButton, EditorFooter, RoadOptionForm } from './RoadOptionForm.js';
import { parseSortOrderInput } from '../../../shared/index.js';
import {
  activateRoadOption,
  archiveRoadOption,
  createRoadOption,
  listRoadOptions,
  patchRoadOption,
} from '../../../api/road-options.js';
import { errorMessage } from '../../../api/errors.js';
import { formatDateTime } from '../../../i18n/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { ROAD_OPTIONS_ACTIVE_QK } from '../../../catalog/index.js';
import { ROAD_OPTIONS_CATALOG_QK } from '../queries.js';

type StatusFilter = 'active' | 'inactive' | 'all';

export function RoadOptionsPage() {
  const { t } = useTranslation(['roadOptions', 'common']);
  // WHY: route is gated on `roads.manage`, but the defensive check
  //   matches the dictionary reference (ArrivalCitiesPage) so any
  //   future relaxation of the route gate cannot silently leak
  //   create/edit/archive affordances to read-only roles.
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'roads.manage');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<RoadOption | null>(null);

  const query = useQuery({
    queryKey: [...ROAD_OPTIONS_CATALOG_QK, status, q],
    queryFn: () => listForFilter(status, q),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>
          {t('roadOptions:title')}
        </h1>
        <ToolbarActions>
          <Input
            value={q}
            onChange={setQ}
            icon="search"
            placeholder={t('roadOptions:search')}
            maxLength={200}
          />
          <Select
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: 'active', label: t('roadOptions:filters.active') },
              { value: 'inactive', label: t('roadOptions:filters.inactive') },
              { value: 'all', label: t('roadOptions:filters.all') },
            ]}
            style={{ width: 150 }}
          />
          {canManage && (
            <Button kind="primary" icon="plus" onClick={() => setCreateOpen(true)}>
              {t('roadOptions:actions.create')}
            </Button>
          )}
        </ToolbarActions>
      </header>
      {query.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(query.error)}
          onRetry={() => void query.refetch()}
        />
      )}
      {query.isPending && <LoadingState />}
      {query.isSuccess && (
        <DataTable
          rows={query.data}
          onSelect={setSelected}
          emptyState={
            <EmptyState title={t('common:states.emptyTitle')} text={t('roadOptions:empty')} />
          }
          columns={[
            { key: 'name', label: t('roadOptions:columns.name'), sortable: true },
            {
              key: 'sortOrder',
              label: t('roadOptions:columns.sortOrder'),
              width: 110,
              sortable: true,
              sortValue: (row) => row.sortOrder,
              render: (row) => (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row.sortOrder}</span>
              ),
            },
            {
              key: 'status',
              label: t('roadOptions:columns.status'),
              width: 130,
              sortable: true,
              sortValue: (row) => (row.active ? 1 : 0),
              render: (row) =>
                row.active ? t('roadOptions:status.active') : t('roadOptions:status.inactive'),
            },
            {
              key: 'createdAt',
              label: t('roadOptions:columns.createdAt'),
              width: 170,
              sortable: true,
              sortValue: (row) => row.createdAt,
              render: (row) => formatDateTime(row.createdAt),
            },
          ]}
        />
      )}
      {createOpen && canManage && <RoadOptionCreateModal onClose={() => setCreateOpen(false)} />}
      {selected && (
        <RoadOptionEditorModal
          row={selected}
          canManage={canManage}
          onClose={() => setSelected(null)}
          onUpdated={setSelected}
        />
      )}
    </div>
  );
}

async function listForFilter(status: StatusFilter, q: string): Promise<RoadOption[]> {
  const base: { q?: string; limit: number } = { limit: 500 };
  if (q.trim()) base.q = q.trim();
  if (status === 'active') return (await listRoadOptions({ ...base, active: true })).items;
  if (status === 'inactive') return (await listRoadOptions({ ...base, active: false })).items;
  // WHY (#1152 Codex F-001): `all` MUST preserve the backend's
  //   canonical `(sortOrder ASC, id ASC)` order. Two parallel
  //   active/inactive fetches concatenated by group would push every
  //   inactive row to the end regardless of sortOrder. Fire one
  //   request without the `active` filter so the backend interleaves
  //   the two states in the right order.
  return (await listRoadOptions({ ...base })).items;
}

function invalidateRoadCaches(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ROAD_OPTIONS_CATALOG_QK });
  void qc.invalidateQueries({ queryKey: ROAD_OPTIONS_ACTIVE_QK });
}

function RoadOptionCreateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['roadOptions', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');

  const payload: CreateRoadOptionInput | null = useMemo(() => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    const parsed = parseSortOrderInput(sortOrder);
    if (parsed === null) return null;
    return { name: trimmed, sortOrder: parsed, active: true };
  }, [name, sortOrder]);

  const mutation = useMutation({
    mutationFn: createRoadOption,
    onSuccess: () => {
      setName('');
      setSortOrder('0');
      invalidateRoadCaches(qc);
      showToast({ tone: 'success', text: t('roadOptions:toasts.created') });
      onClose();
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const dirty = name.trim().length > 0 || sortOrder.trim() !== '0';

  return (
    <EditorModal
      open
      title={t('roadOptions:modals.createTitle')}
      width={420}
      dirty={dirty}
      busy={mutation.isPending}
      onConfirmClose={onClose}
    >
      <RoadOptionForm
        name={name}
        sortOrder={sortOrder}
        active
        onChangeName={setName}
        onChangeSortOrder={setSortOrder}
      />
      <EditorFooter>
        <CancelButton onClose={onClose} label={t('common:actions.cancel')} />
        <Button
          kind="primary"
          disabled={payload === null || mutation.isPending}
          onClick={() => payload && mutation.mutate(payload)}
        >
          {t('common:actions.create')}
        </Button>
      </EditorFooter>
    </EditorModal>
  );
}

function RoadOptionEditorModal({
  row,
  canManage,
  onClose,
  onUpdated,
}: {
  row: RoadOption;
  canManage: boolean;
  onClose: () => void;
  onUpdated: (row: RoadOption) => void;
}) {
  const { t } = useTranslation(['roadOptions', 'common']);
  const qc = useQueryClient();
  const showToast = useToastStore((s) => s.show);
  const [name, setName] = useState(row.name);
  const [sortOrder, setSortOrder] = useState(String(row.sortOrder));

  const patch: PatchRoadOptionInput | null = useMemo(() => {
    const trimmed = name.trim();
    const diff: PatchRoadOptionInput = {};
    if (trimmed && trimmed !== row.name) {
      diff.name = trimmed;
    } else if (!trimmed) {
      return null;
    }
    const parsed = parseSortOrderInput(sortOrder);
    if (parsed === null) return null;
    if (parsed !== row.sortOrder) {
      diff.sortOrder = parsed;
    }
    return Object.keys(diff).length === 0 ? null : diff;
  }, [name, sortOrder, row.name, row.sortOrder]);

  const patchMutation = useMutation({
    mutationFn: (body: PatchRoadOptionInput) => patchRoadOption(row.id, body),
    onSuccess: (next) => {
      setName(next.name);
      setSortOrder(String(next.sortOrder));
      invalidateRoadCaches(qc);
      showToast({ tone: 'success', text: t('roadOptions:toasts.updated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveRoadOption(row.id),
    onSuccess: (next) => {
      invalidateRoadCaches(qc);
      showToast({ tone: 'success', text: t('roadOptions:toasts.archived') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const activateMutation = useMutation({
    mutationFn: () => activateRoadOption(row.id),
    onSuccess: (next) => {
      invalidateRoadCaches(qc);
      showToast({ tone: 'success', text: t('roadOptions:toasts.activated') });
      onUpdated(next);
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const busy = patchMutation.isPending || archiveMutation.isPending || activateMutation.isPending;
  const dirty = patch !== null;

  return (
    <EditorModal
      open
      title={t('roadOptions:modals.editTitle', { name: row.name })}
      width={420}
      dirty={dirty}
      busy={busy}
      onConfirmClose={onClose}
    >
      <RoadOptionForm
        name={name}
        sortOrder={sortOrder}
        active={row.active}
        readOnly={!canManage}
        onChangeName={setName}
        onChangeSortOrder={setSortOrder}
      />
      <EditorFooter>
        {canManage &&
          (row.active ? (
            <Button kind="ghost" disabled={busy} onClick={() => archiveMutation.mutate()}>
              {t('common:actions.archive')}
            </Button>
          ) : (
            <Button kind="ghost" disabled={busy} onClick={() => activateMutation.mutate()}>
              {t('common:actions.restore')}
            </Button>
          ))}
        <CancelButton onClose={onClose} label={t('common:actions.close')} />
        {canManage && (
          <Button
            kind="primary"
            disabled={patch === null || busy}
            onClick={() => patch && patchMutation.mutate(patch)}
          >
            {t('common:actions.save')}
          </Button>
        )}
      </EditorFooter>
    </EditorModal>
  );
}
